import type { WhatsAppTemplate, WhatsAppTemplateAccount } from '@/lib/api/whatsapp-templates.api';

export interface TemplateGroupAccount {
  waba_id: string;
  name: string | null;
  /** The template row on this account, or null when the template is missing there. */
  row: WhatsAppTemplate | null;
}

export interface TemplateGroup {
  key: string;
  name: string;
  language: string;
  category: string;
  rows: WhatsAppTemplate[];
  /** One entry per known account, in the order returned by `/whatsapp/templates/accounts`. */
  accounts: TemplateGroupAccount[];
  missingWabaIds: string[];
  /** Max `updated_at` across the group's rows. */
  lastSyncedAt: string;
  summary: string;
  summaryVariant: 'default' | 'secondary' | 'destructive';
}

export const groupKey = (name: string, language: string) => `${name}::${language}`;

export const accountLabel = (account: { waba_id: string; name: string | null }) =>
  account.name?.trim() || account.waba_id;

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Builds the "Live on 2 of 2 accounts" style summary for a group.
 * `total` is the number of known accounts (from `/accounts` plus any account a row exists on).
 */
export const summarizeGroup = (
  rows: WhatsAppTemplate[],
  total: number,
  missingCount: number,
): { text: string; variant: TemplateGroup['summaryVariant'] } => {
  const byStatus = new Map<string, number>();
  for (const row of rows) {
    byStatus.set(row.status, (byStatus.get(row.status) ?? 0) + 1);
  }
  const approved = byStatus.get('APPROVED') ?? 0;
  const rejected = byStatus.get('REJECTED') ?? 0;

  if (total > 0 && approved === total) {
    return { text: `Live on ${approved} of ${plural(total, 'account')}`, variant: 'default' };
  }

  const parts: string[] = [];
  if (approved > 0) parts.push(`live on ${approved} of ${total}`);
  for (const [status, count] of byStatus) {
    if (status === 'APPROVED') continue;
    parts.push(`${status.toLowerCase()} on ${count}`);
  }
  if (missingCount > 0) parts.push(`missing on ${plural(missingCount, 'account')}`);

  return {
    text: capitalize(parts.join(' · ')),
    variant: rejected > 0 ? 'destructive' : 'secondary',
  };
};

/** Groups template rows by (name, language) and lines each group up against the known accounts. */
export const groupTemplates = (
  rows: WhatsAppTemplate[],
  accounts: WhatsAppTemplateAccount[],
): TemplateGroup[] => {
  const knownAccounts = new Map<string, { waba_id: string; name: string | null }>();
  for (const account of accounts) {
    knownAccounts.set(account.waba_id, { waba_id: account.waba_id, name: account.name });
  }
  // Rows can exist on an account the accounts endpoint does not list (for example after an
  // account was removed); keep them visible rather than silently dropping them.
  for (const row of rows) {
    if (!knownAccounts.has(row.whatsapp_business_account_id)) {
      knownAccounts.set(row.whatsapp_business_account_id, {
        waba_id: row.whatsapp_business_account_id,
        name: null,
      });
    }
  }

  const byKey = new Map<string, WhatsAppTemplate[]>();
  for (const row of rows) {
    const key = groupKey(row.name, row.language);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(row);
    else byKey.set(key, [row]);
  }

  const groups: TemplateGroup[] = [];
  for (const [key, groupRows] of byKey) {
    const rowByWaba = new Map(groupRows.map((row) => [row.whatsapp_business_account_id, row]));
    const groupAccounts: TemplateGroupAccount[] = [];
    for (const account of knownAccounts.values()) {
      groupAccounts.push({ ...account, row: rowByWaba.get(account.waba_id) ?? null });
    }
    const missingWabaIds = groupAccounts.filter((a) => !a.row).map((a) => a.waba_id);
    const summary = summarizeGroup(groupRows, groupAccounts.length, missingWabaIds.length);
    const lastSyncedAt = groupRows
      .map((row) => row.updated_at)
      .reduce((max, value) => (value > max ? value : max));

    groups.push({
      key,
      name: groupRows[0].name,
      language: groupRows[0].language,
      category: groupRows[0].category,
      rows: groupRows,
      accounts: groupAccounts,
      missingWabaIds,
      lastSyncedAt,
      summary: summary.text,
      summaryVariant: summary.variant,
    });
  }

  return groups.sort(
    (a, b) => a.name.localeCompare(b.name) || a.language.localeCompare(b.language),
  );
};
